/*
  Faster collection filters, on top of the theme's facets.min.js (left untouched).

  The theme loads the product grid first and only when it has arrived requests the filter list,
  so the clicked checkbox shows as ticked only after two requests in a row (plus the grid work in
  between). This file:
   1. ticks / unticks the clicked filter value straight away (the theme re-renders the list later
      with the real state);
   2. requests the filter list at the same time as the grid;
   3. starts both requests while the pointer rests on a filter value (hover / touch), so the click
      is often answered at once.
  Responses go into the theme's own cache (Facets.filterData) in the format it stores itself, and
  the theme's renderSection waits for a request already in flight instead of sending it again.
*/
(() => {
  if (window.__uoyFacetsSpeed) return;
  window.__uoyFacetsSpeed = true;

  const LINKS = '[data-sidebar-links] a:not([data-not-a-filter])';
  const sectionUrl = (url, id) => `${url}${url.indexOf('?') > -1 ? '&' : '/?'}section_id=${id}`;

  const patch = (facets) => {
    if (facets.__uoyFast) return;
    facets.__uoyFast = true;

    const inflight = new Map(); // section URL -> promise, settled once the response is cached
    const cached = (key) => (facets.filterData || []).some((d) => d.filterUrl === key);
    const prefetch = (url, id, pick) => {
      if (!url || !id) return;
      const key = sectionUrl(url, id);
      if (cached(key) || inflight.has(key)) return;
      const job = fetch(key, { headers: { 'Cache-Control': 'no-cache' } })
        .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
        .then((html) => {
          const entry = pick(new DOMParser().parseFromString(html, 'text/html'));
          if (entry && !cached(key)) facets.filterData = [...(facets.filterData || []), { ...entry, filterUrl: key }];
        })
        .catch(() => {}) // the theme fetches it itself
        .finally(() => inflight.delete(key));
      inflight.set(key, job);
    };
    const pickGrid = (doc) => {
      const box = doc.querySelector('[data-ntajax-container]');
      if (!box) return null;
      const count = doc.querySelector('[data-result-count]');
      return { DomHTMl: box.innerHTML, resultCount: count ? count.innerHTML : '' };
    };
    const pickFilters = (doc) => {
      const list = doc.querySelector('[data-filter-links]');
      return list ? { DomHTMl: list.innerHTML } : null;
    };
    const hasFilters = () => facets.$filter && facets.$filter.length > 0;
    const warm = (url) => {
      prefetch(url, facets.main, pickGrid);
      if (hasFilters()) prefetch(url, facets.filter, pickFilters);
    };

    // a request we already sent: wait for it, then the theme reads the response from its cache
    const renderSection = facets.renderSection;
    facets.renderSection = function (url, id, cb) {
      const job = inflight.get(sectionUrl(url, id));
      if (job) { job.then(() => renderSection.call(this, url, id, cb)); return; }
      renderSection.call(this, url, id, cb);
    };

    // filter clicks: ask for the grid and the filter list together
    const loadPageContent = facets.loadPageContent;
    facets.loadPageContent = function (url) {
      if (this.isReloadFilter && hasFilters()) {
        prefetch(url, this.main, pickGrid);
        prefetch(url, this.filter, pickFilters);
      }
      return loadPageContent.call(this, url);
    };

    facets.__uoyWarm = warm;
  };

  // the theme keeps each instance on its [data-ntajax-options] element
  const instances = () => [...document.querySelectorAll('[data-ntajax-options]')].map((el) => el.Facets).filter(Boolean);
  const facetsFor = (link) => {
    const list = instances();
    return list.find((f) => f.$section && f.$section[0] && f.$section[0].contains(link)) || list[0];
  };

  // tick the clicked value at once (sidebar and drawer lists)
  document.addEventListener('click', (e) => {
    const link = e.target.closest && e.target.closest(LINKS);
    if (!link || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const facets = facetsFor(link);
    if (!facets || !facets.__uoyFast) return;
    const item = link.closest('li');
    if (item && !item.classList.contains('is--disabled')) item.classList.toggle('is--selected');
  }, true);

  // warm up the grid + filter list for the value under the pointer / finger
  let hoverTimer = 0;
  let lastScroll = 0;
  const warmLink = (link) => {
    const facets = facetsFor(link);
    if (facets && facets.__uoyWarm) facets.__uoyWarm(link.getAttribute('href'));
  };
  // a value that slides under a still mouse while the page scrolls (e.g. the theme scrolling up
  // after a filter) is not one the visitor is aiming at
  window.addEventListener('scroll', () => { lastScroll = performance.now(); }, { passive: true });
  document.addEventListener('pointerover', (e) => {
    if (e.pointerType !== 'mouse') return;
    const link = e.target.closest && e.target.closest(LINKS);
    clearTimeout(hoverTimer);
    if (link) hoverTimer = setTimeout(() => { if (performance.now() - lastScroll > 400) warmLink(link); }, 100);
  }, { passive: true });
  document.addEventListener('touchstart', (e) => {
    const link = e.target.closest && e.target.closest(LINKS);
    if (link) warmLink(link);
  }, { passive: true });

  // the theme loads facets.min.js on demand: patch each instance once it exists
  let tries = 0;
  const waitForFacets = () => {
    instances().forEach(patch);
    const pending = [...document.querySelectorAll('[data-ntajax-options]')].some((el) => !el.Facets || !el.Facets.__uoyFast);
    if (pending && ++tries < 150) setTimeout(waitForFacets, 200);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', waitForFacets, { once: true });
  else waitForFacets();
})();
